/**
 * Readiness & Verification — RV2.
 *
 * Intent-weighted provider-impact matrix. A failed provider's IMPACT on
 * the report depends on what the project is actually trying to do:
 *
 *   - waterStress missing + wet cooling   → significant (cooling is gated)
 *   - waterStress missing + air cooling   → minor      (only aux loads use water)
 *   - nuclearInfra missing + nuclear=no   → irrelevant (no EPZ analysis needed)
 *   - nuclearInfra missing + nuclear=yes  → significant (NRC data drives EPZ)
 *
 * This module exposes two helpers:
 *
 *   - `providerImpactForIntent(provider, intent)` — the raw matrix.
 *     Returns a fresh impact + reason for any (provider, intent) pair.
 *   - `effectiveGapImpact(gap, intent)` — the entry point report-side
 *     callers use. Reads the raw SED `DataGap.impact` AND re-weights it
 *     against intent, so an already-reported significant gap on a
 *     provider the project doesn't care about decays to `irrelevant`.
 *
 * The helper is pure and side-effect free. Imported by:
 *   - RV1 honest-tier cap (counts significant + critical gaps)
 *   - RV3 SectionReadinessPanel (per-row tier + reason)
 *   - RV4 remediationHints (only generates hints for non-irrelevant gaps)
 *   - RV-B narrative renderer (section-level Watch banners)
 */
import type { DataGap, SedSectionId } from '../types/siteEvidence';
import type { ProjectIntent } from '../types/siteRag';
import { WATER_WITHDRAWING_SINKS } from './reportGeneration/reviewScoring/extractCrossFieldFindings';

/**
 * Provider/section identifier. Aligned with `SedSectionId` from
 * `types/siteEvidence` so a SED slot maps 1:1 to a provider entry here.
 * Kept as a string alias rather than re-exporting the SED type so this
 * file stays the canonical owner of the impact-matrix surface.
 */
export type ProviderId = SedSectionId;

/**
 * Intent-aware severity. Strict superset of the SED `DataGap.impact`
 * vocabulary (`'critical' | 'significant' | 'minor'`) plus `'irrelevant'`
 * — the new tier we add for "this provider's data doesn't matter for
 * THIS project, suppress the gap from the user's gap list".
 */
export type ImpactSeverity = 'critical' | 'significant' | 'minor' | 'irrelevant';

export interface ProviderImpactAssessment {
  severity: ImpactSeverity;
  /** Single-sentence, user-facing reason for the assigned severity. */
  reason: string;
}

/* ── Intent feature derivations ─────────────────────────────────────── */

/** Wet cooling = the facility WITHDRAWS water for heat rejection.
 *
 *  Water withdrawal is driven by the cold-END heat-rejection SINK
 *  (`bopHeatRejection`), NOT the orthogonal rack-side `coolingStrategy`
 *  (air vs liquid) — a liquid-rack + dry-cooled site withdraws ~no water; an
 *  air-rack + wet-tower site withdraws a lot. This reads the SAME
 *  `WATER_WITHDRAWING_SINKS` set the cross-field water-conflict guard and the
 *  Path-to-GO water gate use, so the SED-gap water relevance can never drift
 *  from them.
 *
 *  Resolution (mirrors the guard / gate):
 *    - `bopHeatRejection` SET → authoritative: wet iff the sink withdraws water.
 *      An explicit DRY sink (dry-cooler / free-cooling / absorption-cchp /
 *      unknown) is NOT wet, even under liquid-rack cooling.
 *    - `bopHeatRejection` UNSET → fall back to the rack-side `coolingStrategy`.
 *      `undecided` / absent is treated as wet to FAIL-SAFE — we'd rather
 *      over-flag a water gap than silently call it irrelevant on a project that
 *      might yet pick a wet sink. */
function isWetCooling(intent: ProjectIntent | null | undefined): boolean {
  const sink = intent?.bopHeatRejection;
  if (sink != null) {
    // Authoritative cold-end signal — wet iff the sink withdraws water.
    return WATER_WITHDRAWING_SINKS.has(sink);
  }
  const c = intent?.coolingStrategy;
  return c === 'liquid' || c === 'hybrid' || c === 'undecided' || c == null;
}

/** Nuclear is "active" when the user has flagged primary or considering.
 *  `no` and `undecided` are both treated as not-active — for an
 *  undecided project we don't want to surface a `significant` nuclear
 *  gap before the user has even said they care. */
function isNuclearActive(intent: ProjectIntent | null | undefined): boolean {
  const n = intent?.nuclearInterest;
  return n === 'primary' || n === 'considering';
}

/** Nuclear is "actively rejected" — i.e. the user explicitly said no.
 *  Used to demote nuclear-only providers to `irrelevant` (vs. `minor`
 *  for undecided, where the user might still pivot). */
function isNuclearRejected(intent: ProjectIntent | null | undefined): boolean {
  return intent?.nuclearInterest === 'no';
}

/* ── Matrix ────────────────────────────────────────────────────────── */

/**
 * Per-provider impact-for-intent matrix. Returns the severity + a
 * one-sentence reason for the gap. Pure, deterministic — same inputs
 * always produce the same output.
 *
 * Rule of thumb:
 *   - `critical`    — report should not ship without this data.
 *   - `significant` — one full report section runs on baseline; reviewer
 *                     should independently verify before acting.
 *   - `minor`       — graceful fallback exists (regional average, etc.).
 *   - `irrelevant`  — intent makes the provider's domain not apply at all.
 */
export function providerImpactForIntent(
  provider: ProviderId,
  intent: ProjectIntent | null | undefined,
): ProviderImpactAssessment {
  const wet = isWetCooling(intent);
  const nuclearActive = isNuclearActive(intent);
  const nuclearRejected = isNuclearRejected(intent);

  switch (provider) {
    case 'climate':
      // Climate drives cooling design + peak load; cannot be skipped.
      return { severity: 'significant', reason: 'Climate drives cooling design basis and peak-load assumptions across every workload profile.' };

    case 'waterStress':
      return wet
        ? { severity: 'significant', reason: 'Project uses wet or hybrid cooling; basin water availability gates feasibility.' }
        : { severity: 'minor', reason: 'Project uses air cooling; water gap only affects auxiliary loads.' };

    case 'seismic':
      // Seismic risk applies to every heavy-industrial siting decision.
      return { severity: 'significant', reason: 'Seismic class gates structural cost and (for nuclear) NRC siting acceptance.' };

    case 'geotechnical':
      return {
        severity: 'significant',
        reason: 'Regional soil screening informs early foundation risk, but does not replace a site-specific geotechnical investigation.',
      };

    case 'grid':
      // Every project needs to know the grid story, even BTM-primary
      // projects need it as the fallback / cross-check.
      return { severity: 'critical', reason: 'Grid proximity and capacity gate interconnection cost and timeline for every workload profile.' };

    case 'flood':
      return { severity: 'significant', reason: 'Flood zone classification gates insurability, FEMA permitting, and slab-elevation cost.' };

    case 'environmental':
      return { severity: 'significant', reason: 'Protected-species and wetlands footprint gate NEPA / Section 404 permitting timeline.' };

    case 'nuclearInfra':
      if (nuclearActive) {
        return { severity: 'significant', reason: 'Nuclear strategy active; NRC reactor-status data drives EPZ analysis and licensing pathway.' };
      }
      if (nuclearRejected) {
        return { severity: 'irrelevant', reason: 'Project Intent rejects nuclear — gap does not affect this report.' };
      }
      // Undecided — surface but at minor severity so the user knows
      // it would matter if they pivot to SMR/considering.
      return { severity: 'minor', reason: 'Nuclear interest is undecided; gap would matter only if the strategy pivots to SMR.' };

    case 'solar':
      // Solar+BESS comparison falls back to NREL Atlas baseline; never
      // a blocker on its own. Stays minor regardless of intent.
      return { severity: 'minor', reason: 'Solar+BESS comparison falls back to NREL Atlas regional baseline if the provider is missing.' };

    case 'wind':
      return { severity: 'minor', reason: 'Wind+BESS comparison uses regional capacity factor if the provider is missing.' };

    case 'electricity':
      // Tariffs/EIA data drives LCOE; significant on every project.
      return { severity: 'significant', reason: 'Tariff data drives the grid-LCOE comparator that every BTM-vs-grid decision rests on.' };

    case 'airQuality':
      // NSR permitting depends on attainment status. Heavier weight
      // for water-cooled / nuclear sites (PSD trigger thresholds bind
      // harder; cooling-tower drift adds a PM10 source).
      return wet || nuclearActive
        ? { severity: 'significant', reason: 'Wet-cooling drift or nuclear emergency planning makes NAAQS attainment status a permit-path dependency.' }
        : { severity: 'minor', reason: 'Air-cooled, non-nuclear project; air quality only marginally affects siting.' };

    case 'connectivity':
      // Fiber + carrier diversity matters for every data-center workload.
      return { severity: 'significant', reason: 'Fiber proximity and carrier diversity gate uptime SLAs for every data-center workload.' };

    case 'parcel':
      // Parcel boundary + ownership matters for every project.
      return { severity: 'significant', reason: 'Parcel boundary and ownership lineage gate every site-design and acquisition decision.' };

    case 'regulatory':
      return { severity: 'significant', reason: 'State and local statutes gate permitting timeline and incentive eligibility.' };

    case 'technology':
      return { severity: 'minor', reason: 'Technology baseline defaults still allow a coherent first-pass recommendation.' };

    // v1.2.1 HR-5 — BTM grounding sections (intent-stamped + provider-grounded).
    case 'cooling':
      return { severity: 'significant', reason: 'Cooling configuration (3 axes) drives PUE, water withdrawal, and BoP sizing; a missing cold-end sink blocks water-resource grounding.' };

    case 'power':
      return { severity: 'significant', reason: 'Power semantics (IT-load vs facility vs MVA) anchor the load → nameplate economics; absence forces a basis assumption.' };

    case 'firmCapacity':
      return { severity: 'minor', reason: 'Firm-capacity (ELCC) intent refines grid-value framing; defaults still allow a coherent first pass.' };

    case 'lcoe':
      return { severity: 'significant', reason: 'LCOE inputs anchor the economics; a missing fuel-cost basis caps the financial conclusion.' };

    case 'fuelSupply':
      return { severity: 'significant', reason: 'Natural-gas price + pipeline proximity ground the gas-BTM fuel cost; absence forces an assumed gas LCOE (relevant only on a gas pathway).' };

    // SED D2 — Interconnection + Incentives evidence sections.
    case 'interconnection':
      return { severity: 'significant', reason: 'ISO/RTO queue position + declared interconnection status ground the power-availability verdict; absence leaves grid-access timing unanchored.' };

    case 'incentives':
      return { severity: 'minor', reason: 'IRA tax-credit (§45Y / §48E) eligibility refines the financial framing; defaults still allow a coherent first pass and it never blocks a section.' };

    // SED D2 (H-6) — Multi-hazard + transmission-headroom + water-resource.
    case 'multiHazard':
      // FEMA NRI wildfire / strong-wind / hail / drought + composite, beyond
      // the always-present flood + seismic sections. Refines the hazard
      // framing; never blocks a section (and is honestly absent off-territory).
      return { severity: 'minor', reason: 'FEMA National Risk Index (wildfire / strong-wind / hail / drought + composite) refines the multi-hazard framing beyond flood + seismic; absence (e.g. non-US site) leaves those hazards unquantified but does not block a section.' };

    case 'transmissionHeadroom':
      // Honest-unavailable typed section today (no in-repo ATC / hosting-
      // capacity / nodal-LMP source). It NEVER blocks: it is informational
      // until a real fetcher lands.
      return { severity: 'minor', reason: 'Transmission headroom (ATC / hosting capacity / nodal LMP) has no point-queryable public source wired; the section is honestly empty and does not block any conclusion.' };

    case 'waterResource':
      // Honest-unavailable typed section today (state-administered / well-id-
      // keyed sources). Distinct from the basin-level waterStress axis, which
      // still grounds the water framing. Never blocks.
      return { severity: 'minor', reason: 'Site-specific water resource (consumptive use / water rights / aquifer depth / NPDES) has no point-queryable public source wired; the basin-level waterStress axis still grounds the water framing.' };

    case 'topography':
      return { severity: 'significant', reason: 'Parcel-scale terrain screening is needed for test-fit grading, access, and drainage-risk framing; no cut/fill conclusion is allowed without it.' };

    case 'survey':
      return { severity: 'significant', reason: 'A current boundary/topographic or ALTA survey is required to rely on legal description, easements, access, and commissioned contour data.' };

    case 'mineralResources':
      return { severity: 'minor', reason: 'Public mineral occurrences are regional screening only; mineral ownership and reservations require a title or county-record report.' };

    case 'culturalResources':
      return { severity: 'significant', reason: 'Public listed-property screening cannot establish archaeological clearance; agency, SHPO/THPO, or professional survey evidence may be required.' };

    case 'siteResearch':
      // Curated knowledge base — usually a "boost" not a "gate". Minor.
      return { severity: 'minor', reason: 'Curated site research supplements provider data; gap reduces precedent depth but does not block a section.' };

    case 'adamsDocuments':
      // Only matters when nuclear is active.
      if (nuclearActive) {
        return { severity: 'significant', reason: 'Nuclear strategy active; ADAMS document trail informs licensing precedent.' };
      }
      if (nuclearRejected) {
        return { severity: 'irrelevant', reason: 'Project Intent rejects nuclear — ADAMS docs do not affect this report.' };
      }
      return { severity: 'minor', reason: 'Nuclear interest undecided; ADAMS docs are reference-only until the strategy resolves.' };

    case 'siteProfile':
      // Boundary + brief — user-driven, not a provider per se, but if
      // it's missing the entire report runs on placeholders.
      return { severity: 'significant', reason: 'Site boundary and project brief anchor every downstream section.' };

    case 'onSiteInfrastructure':
      // KSD1 — KMZ-derived facts. Missing means the user didn't upload
      // a KMZ; report quality degrades only modestly because the live
      // providers still cover the territory.
      return { severity: 'minor', reason: 'On-site infrastructure KMZ not uploaded — provider data covers substation, fiber, and water proximity adequately.' };

    case 'externalLiterature':
      // LL1 — live retrieval of current peer-reviewed + preprint sources.
      // Always 'minor': the curated `peerReviewDb` knowledge base is the
      // citation spine; live literature is additive enrichment. A failed
      // retrieval reduces citation freshness but doesn't block any section.
      return { severity: 'minor', reason: 'Live literature retrieval (Crossref/arXiv/PubMed) supplements the curated knowledge base; gap reduces freshness of citations but does not block any section.' };

    case 'publicSentiment':
      // PSE-1 — public sentiment is additive to the scoring axis but the
      // axis itself is gated on the section being present (weight
      // redistributes when missing). A retrieval gap therefore degrades
      // the feasibility overall in a controlled way and doesn't block
      // any narrative section.
      return { severity: 'minor', reason: 'Public sentiment retrieval (GDELT, council minutes, federal dockets) supplements the feasibility scoring axis; absence redistributes weight to the other five categories.' };

    case 'economicEvidence':
      return { severity: 'minor', reason: 'Economic, workforce, and comparable evidence is additive screening context; absence does not block core site diligence.' };

    default: {
      // Exhaustive guard — TS will fail this branch if a new SedSectionId
      // is added without extending the matrix above.
      const _exhaustive: never = provider;
      void _exhaustive;
      return { severity: 'minor', reason: 'Provider impact not classified; defaulting to minor.' };
    }
  }
}

/* ── Gap-aware entry point ─────────────────────────────────────────── */

/**
 * Re-weight a raw SED gap against the project's intent.
 *
 * The SED's `DataGap.impact` is intent-agnostic — it captures whether a
 * provider missed data, not whether the project cares. This helper
 * reads the raw impact AND the matrix above and returns the EFFECTIVE
 * severity for THIS project.
 *
 * Resolution rule (intent-aware severity is authoritative for known
 * providers):
 *   - The matrix is the single source of truth for "does this project
 *     care about this provider". It always wins when the matrix demotes
 *     a raw `critical` / `significant` gap (e.g. waterStress on a
 *     dry-cooled project → `minor`; nuclearInfra on a no-nuclear
 *     project → `irrelevant`).
 *   - Exception: if the raw SED already flagged the gap as `critical`
 *     AND the matrix says `significant` (i.e. both classifiers agree
 *     the project cares, but the raw signal is even worse), keep the
 *     raw `critical` — the cross-validation has surfaced something
 *     deeper than the matrix can see.
 *
 * If the gap cannot be mapped to a known provider (missing `providerId`
 * AND no `field` match in `SED_SECTION_IDS`), fall back to the raw
 * `gap.impact` so we never silently lose a gap.
 */
export function effectiveGapImpact(
  gap: Pick<DataGap, 'impact' | 'providerId' | 'field'>,
  intent: ProjectIntent | null | undefined,
): ProviderImpactAssessment {
  const providerId = resolveProviderId(gap);
  if (!providerId) {
    return {
      severity: gap.impact,
      reason: `Raw SED impact: ${gap.impact}.`,
    };
  }

  const matrix = providerImpactForIntent(providerId, intent);
  const merged = mergeSeverity(gap.impact, matrix.severity);
  return {
    severity: merged,
    // Always prefer the intent-aware reason — it's the actionable one.
    reason: matrix.reason,
  };
}

/** Map a raw gap to a known provider id. Prefers the explicit
 *  `providerId` field (added in Content-Source Fixes Prompt 10); falls
 *  back to a substring match against the `field` so legacy SEDs still
 *  resolve. */
function resolveProviderId(gap: Pick<DataGap, 'providerId' | 'field'>): ProviderId | null {
  if (gap.providerId) {
    const direct = matchProviderId(gap.providerId);
    if (direct) return direct;
  }
  const field = gap.field?.toLowerCase() ?? '';
  for (const id of PROVIDER_IDS) {
    if (field.includes(id.toLowerCase())) return id;
  }
  return null;
}

/** Heuristic provider-id mapper — turns legacy/free-form provider
 *  strings ('nrel-wind', 'WRI Aqueduct', 'airnow') into canonical
 *  `ProviderId` values. */
function matchProviderId(raw: string): ProviderId | null {
  const r = raw.toLowerCase();
  if (r.includes('water') || r.includes('aqueduct')) return 'waterStress';
  if (r.includes('seismic') || r.includes('usgs')) return 'seismic';
  if (r.includes('grid') || r.includes('hifld')) return 'grid';
  if (r.includes('flood') || r.includes('fema')) return 'flood';
  if (r.includes('environ') || r.includes('usfws') || r.includes('nepa')) return 'environmental';
  if (r.includes('nuclear') || r.includes('nrc')) return 'nuclearInfra';
  if (r.includes('solar') || r.includes('nrel-solar') || r.includes('pv')) return 'solar';
  if (r.includes('wind') || r.includes('nrel-wind')) return 'wind';
  if (r.includes('elect') || r.includes('eia') || r.includes('tariff')) return 'electricity';
  if (r.includes('air') || r.includes('airnow') || r.includes('naaqs')) return 'airQuality';
  if (r.includes('connect') || r.includes('fiber') || r.includes('carrier')) return 'connectivity';
  if (r.includes('parcel') || r.includes('cadastral')) return 'parcel';
  if (r.includes('reg') || r.includes('statute')) return 'regulatory';
  if (r.includes('climate') || r.includes('noaa')) return 'climate';
  if (r.includes('adams')) return 'adamsDocuments';
  if (r.includes('site-research') || r.includes('siteresearch')) return 'siteResearch';
  if (r.includes('profile')) return 'siteProfile';
  return null;
}

/** Severity ordering, low → high. Exported so downstream readers
 *  (RV1 honest tier, RV3 SectionReadinessPanel, RV4 hint registry) can
 *  rank severities consistently. `'irrelevant'` is the lowest tier so a
 *  `Math.max`-style worst-of-list works naturally. */
export const SEVERITY_ORDER: Record<ImpactSeverity, number> = {
  irrelevant: 0,
  minor: 1,
  significant: 2,
  critical: 3,
};

function mergeSeverity(raw: DataGap['impact'], matrix: ImpactSeverity): ImpactSeverity {
  // The matrix is authoritative — its job is precisely to re-weight
  // intent-agnostic raw impacts against what THIS project needs. The
  // one exception is when both classifiers agree the project cares
  // (matrix says `significant` or higher) AND the raw SED escalated
  // to `critical`; in that case the cross-validation has surfaced
  // something the matrix can't see, so keep the raw `critical`.
  if (raw === 'critical' && matrix === 'significant') return 'critical';
  return matrix;
}

/** Static list mirrored from `SED_SECTION_IDS`. Duplicated here as a
 *  runtime constant so this module doesn't pull the full type-only
 *  `siteEvidence.ts` graph into bundles that only need the matrix. */
const PROVIDER_IDS: readonly ProviderId[] = [
  'climate',
  'waterStress',
  'seismic',
  'grid',
  'flood',
  'environmental',
  'nuclearInfra',
  'solar',
  'wind',
  'electricity',
  'airQuality',
  'connectivity',
  'parcel',
  'regulatory',
  'technology',
  'siteResearch',
  'adamsDocuments',
  'siteProfile',
  'onSiteInfrastructure',
] as const;
